import { useEffect, useState } from 'react';

const CLOSED_MENU_STATE = {
  context: null,
  menuPos: { x: null, y: null },
  show: false,
};

/**
 * Custom hook that manages the scheduler context menu state and closes the
 * menu when the user clicks, scrolls, or right-clicks anywhere else, or
 * presses Escape.
 *
 * @param {React.RefObject} contextMenuRef - Ref to the context menu DOM element
 */
function useCustomContextMenu(contextMenuRef) {
  const [contextMenu, setContextMenu] = useState(CLOSED_MENU_STATE);

  /**
   * @param {Object} options
   * @param {import('../OpSchedulerNext.types').ContextMenuContext} options.context
   * @param {{ x: number, y: number }} options.menuPos
   */
  const handleShowCustomContextMenu = ({ context, menuPos }) => {
    setContextMenu({
      context,
      menuPos,
      show: true,
    });
  };

  const handleHideCustomContextMenu = () => {
    setContextMenu(CLOSED_MENU_STATE);
  };

  useEffect(() => {
    // Check if context menu is open
    if (!contextMenu.show) {
      return undefined;
    }

    const handleInteractionOutside = (e) => {
      // Close before a new context-menu event opens another menu. The menu
      // opening event itself occurs before the new menu ref is mounted.
      if (contextMenuRef?.current && !contextMenuRef.current.contains(e.target)) {
        handleHideCustomContextMenu();
      }
    };

    const handleKeyDown = (e) => {
      // Close menu on Escape key press
      if (e.key === 'Escape') {
        handleHideCustomContextMenu();
      }
    };

    // Attach event listeners
    document.addEventListener('click', handleInteractionOutside);
    document.addEventListener('mousedown', handleInteractionOutside);
    document.addEventListener('scroll', handleHideCustomContextMenu, true); // Use capture phase for scroll
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listeners
    return () => {
      document.removeEventListener('click', handleInteractionOutside);
      document.removeEventListener('mousedown', handleInteractionOutside);
      document.removeEventListener('scroll', handleHideCustomContextMenu, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu.show, contextMenuRef]);

  return { contextMenu, handleShowCustomContextMenu, handleHideCustomContextMenu };
}

export default useCustomContextMenu;
